import { useRef, useState } from "react"
import { useApi } from "../api/hooks"
import {
  processReceipt,
  requestUploadUrl,
  uploadToS3,
  type ProcessResponse,
} from "../api/receipts"
import { formatBytes } from "../lib/format"
import { useT } from "../lib/i18n"
import { ErrorModal } from "./ErrorModal"

const ALLOWED = ["image/jpeg", "image/png", "image/heic", "image/heif", "application/pdf"]
const MAX_BYTES = 6 * 1024 * 1024

export function UploadFab({ onUploaded, onProcessed }: { onUploaded: (receiptId: string) => void; onProcessed?: (res: ProcessResponse) => void }) {
  const api = useApi()
  const { t } = useT()
  const inputRef = useRef<HTMLInputElement>(null)
  const lastFile = useRef<File | null>(null)
  const [busy, setBusy] = useState<"idle" | "presign" | "s3" | "process">("idle")
  const [err, setErr] = useState<unknown>(null)

  async function onPick(file: File) {
    setErr(null)
    lastFile.current = file
    if (!ALLOWED.includes(file.type)) {
      setErr(new Error(`${t("unsupported_type")}: ${file.type}`))
      return
    }
    if (file.size > MAX_BYTES) {
      setErr(new Error(`${t("too_large")}: ${formatBytes(file.size)} (max ${formatBytes(MAX_BYTES)})`))
      return
    }
    try {
      setBusy("presign")
      const presign = await requestUploadUrl(api, file.name, file.type, file.size)
      setBusy("s3")
      await uploadToS3(presign.put_url, file, file.type)
      setBusy("process")
      const res = await processReceipt(api, presign.receipt_id)
      setBusy("idle")
      onUploaded(presign.receipt_id)
      onProcessed?.(res)
    } catch (e: unknown) {
      setErr(e)
      setBusy("idle")
    }
  }

  const label = busy === "presign" ? t("uploading")
    : busy === "s3" ? t("uploading")
    : busy === "process" ? t("extracting")
    : "+"

  return (
    <>
      <button
        aria-label={t("add_receipt")}
        onClick={() => inputRef.current?.click()}
        disabled={busy !== "idle"}
        style={{
          position: "fixed",
          bottom: 96,
          right: 24,
          width: 56,
          height: 56,
          borderRadius: "50%",
          background: "var(--accent)",
          color: "var(--accent-fg)",
          fontSize: busy === "idle" ? 30 : 11,
          border: "none",
          cursor: busy === "idle" ? "pointer" : "wait",
          boxShadow: "0 10px 28px rgba(0,0,0,0.4)",
          zIndex: 999,
        }}
      >
        {label}
      </button>
      <input
        ref={inputRef}
        type="file"
        accept={ALLOWED.join(",")}
        capture="environment"
        hidden
        onChange={(e) => {
          const f = e.target.files?.[0]
          if (f) void onPick(f)
          e.target.value = ""
        }}
      />
      <ErrorModal
        error={err}
        title={t("upload_failed")}
        onClose={() => setErr(null)}
        onRetry={lastFile.current ? () => { const f = lastFile.current; if (f) void onPick(f) } : undefined}
      />
    </>
  )
}
